import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Input, Row, Col, Card, CardBody, Button, Spinner } from "reactstrap"; // Added Spinner

import CreatableSelect from 'react-select/creatable';import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from "react-redux";
import Select from 'react-select';
import Multiselect from "multiselect-react-dropdown";
import { addNewTag, fetchParentCategories, fetchSubCategories } from "../../../slices/thunks";

const defaultColor = "#000000"; // Default color for the color picker

const EditDocDetails = ({ labels, formikProps, onTranslateClickHandler, translationLoading, lastTranslatedField, isAddOrEdit }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = i18n.dir() === 'rtl'
    const today = new Date().toISOString().split("T")[0];

    const [allTags, setAllTags] = useState([]);
    const [tagInputValue, setTagInputValue] = useState("");
    const [isTagCreating, setIsTagCreating] = useState(false); 

  // Destructure from formikProps for easier access
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formikProps;

    const { 
      tags, 
      tagCreationLoading,
      subCategories,
      subCategoriesLoading,
    } = useSelector((state) => state.AIDoc);
    
    const { 
        parentCategories, 
        loading: categoriesLoading 
    } = useSelector((state) => state.Category);

    // --- NEW: Fetch parent categories on component mount ---
    useEffect(() => {
        dispatch(fetchParentCategories({ DocType: 4 }));
    }, [dispatch]);

    // --- NEW: Fetch sub-categories when CategoryID changes ---
    useEffect(() => {
        if (values.CategoryID) {
            dispatch(fetchSubCategories({ DocType: 4, ParentCategoryID: values.CategoryID }));
        }
    }, [values.CategoryID, dispatch]);

    useEffect(() => {
        if (tags) {
            const formattedTags = tags.map(tag => ({ value: tag.ID, label: tag.TagName }));
            setAllTags(formattedTags);
        }
    }, [tags]);

    useEffect(() => {
    if (Array.isArray(values.TagID) && allTags.length > 0) {
        // If TagID contains numbers, map to objects
        if (typeof values.TagID[0] === "number") {
            const mapped = values.TagID
                .map(id => allTags.find(tag => tag.value === id))
                .filter(Boolean);
            setFieldValue('TagID', mapped);
        }
    }
    // eslint-disable-next-line
    }, [allTags]);

    const handleCreateTag = async (inputValue) => {
        if (!inputValue) return;
        setIsTagCreating(true);
        try {
            const newTag = await dispatch(addNewTag({ TagName: inputValue })).unwrap();
            
            // --- CHANGE 1: Always store only the ID in Formik state ---
            // This keeps the data format consistent.
        const currentSelected = values.TagID || [];
        setFieldValue('TagID', [...currentSelected, { value: newTag.ID, label: newTag.TagName }]);

            setTagInputValue(''); 
        } catch (e) {
            console.error("Failed to create tag:", e);
            toast.error(t("Failed to create tag"));
        } finally {
            setIsTagCreating(false);
        }
    };

    // --- NEW: Options for Select dropdowns ---
    // const parentCategoriesOptions = parentCategories.map(cat => ({
    //     value: cat.ID,
    //     label: isRTL ? cat.NameAr : cat.NameEn
    // }));

    // const subCategoriesOptions = subCategories.map(sub => ({
    //     value: sub.ID,
    //     label: isRTL ? sub.CategoryNameAr : sub.CategoryNameEn
    // }));

    const parentCategoriesOptions = (parentCategories || []).map(cat => ({
      value: cat.ID,
      label: isRTL ? cat.NameAr?.trim() : cat.NameEn?.trim(),
      Category: cat.NameEn?.trim() || '',
    }));
    
    const subCategoriesOptions = (subCategories || []).map(cat => ({
      value: cat.ID,
      label: isRTL ? cat.CategoryNameAr?.trim() : cat.CategoryNameEn?.trim(),
      SubCategory: cat.CategoryNameEn?.trim() || '',
    }));

  const renderField = (item) => {
    const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit) : !!item.disabled;
    const fieldValue = values[item.name] ?? "";
     const getSelectStyles = (isInvalid) => ({
            control: (base, state) => ({
                ...base,
                borderColor: isInvalid ? '#f46a6a' : state.isFocused ? '#80bdff' : '#ced4da',
                '&:hover': { borderColor: isInvalid ? '#f46a6a' : '#adb5bd' },
                boxShadow: isInvalid ? '0 0 0 0.2rem rgba(244, 106, 106, 0.25)' : (state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none'),
                direction: isRTL ? 'rtl' : 'ltr',
            }),
            menu: base => ({ ...base, zIndex: 3, textAlign: isRTL ? 'right' : 'left' }),
            multiValue: base => ({ ...base, direction: isRTL ? 'rtl' : 'ltr' }),
     });

    switch (item.fieldName) {
      case "text-box":
        return (
            <Input
            type={"text"}
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={(e) => {
              const inputValue = e.target.value;
              
              // If it's RoleName field, allow only letters and spaces
              if (item.name === "RoleNameEn" || item.name === "RoleNameAr") {
                const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                setFieldValue(item.name, cleanedValue);
              } else {
                handleChange(e);
              }
            }}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            disabled={isDisabled}
            required={item.required}
          />
        );
    
      case "email-box":
        return (
          <Input
            type={"email"}
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case "number":
        return (
          <Input
            type="tel"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange} 
            onBlur={handleBlur}
            className="form-control"
            // maxLength={maxLength}
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case 'number-box':
        return (
            <Input
                type="text"
                style={{ textAlign: isRTL ? 'right' : 'left' }}
                id={item.name}
                name={item.name}
                placeholder={t(item.placeholder || item.labelName)}
                value={fieldValue}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  let filteredValue = inputValue.replace(/[^0-9.]/g, '');

                  const parts = filteredValue.split('.');
                  if (parts.length > 2) {
                      filteredValue = parts[0] + '.' + parts.slice(1).join('');
                  }
                  setFieldValue(item.name, filteredValue);
                }}
                onBlur={handleBlur}
                className="form-control"
                invalid={touched[item.name] && !!errors[item.name]}
                disabled={isDisabled}
                required={item.required}
            />
        );

        case "textarea":
        return (
          <Input
            type="textarea"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Enforce min/max length in UI (optional)
              if (item.maxLength && inputValue.length > item.maxLength) return;
              handleChange(e);
            }}
            onBlur={handleBlur}
            className="form-control"
            invalid={
              (touched[item.name] && !!errors[item.name]) ||
              (item.minLength && fieldValue.length < item.minLength) ||
              (item.maxLength && fieldValue.length > item.maxLength)
            }
            style={{ height: "120px" }}
            disabled={isDisabled}
            required={item.required}
            minLength={item.minLength}
            maxLength={item.maxLength}
          />
        );


      case "text-box-translate":
        return (
          <div className="input-group"> 
            <Input
              type={"text"}
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue}
              onChange={(e) => {
                const inputValue = e.target.value;
                
                // If it's RoleName field, allow only letters and spaces
                if (item.name === "RoleNameEn" || item.name === "RoleNameAr") {
                  const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                  setFieldValue(item.name, cleanedValue);
                } else {
                  handleChange(e);
                }
              }}            
              maxLength={20}
              minLength={2}
              onBlur={handleBlur}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name]}
              required={item.required}
            />
            {onTranslateClickHandler && item.translateTo && (
              <Button
                type="button"
                onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)} // Pass item.name as sourceFieldName
                disabled={!fieldValue || !fieldValue.trim() || translationLoading} // Disable if any translation is loading
              >
                {translationLoading && lastTranslatedField === item.name ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-translate"></i>
                )}
              </Button>
            )}
          </div>
        );
      
      case "select-category": {
          const isInvalid = touched[item.name] && !!errors[item.name];
          const selectedValue = parentCategoriesOptions.find(opt => String(opt.value) === String(fieldValue)) || null;

          return (
              <Select
                  id={item.name}
                  name={item.name}
                  options={parentCategoriesOptions}
                  value={selectedValue}
                  onChange={(selectedOption) => {
                      // Set category value and reset sub-category
                      setFieldValue(item.name, selectedOption ? selectedOption.value : '');
                      setFieldValue('SubCategoryID', ''); // Assumes sub-category field name is 'SubCategoryID'
                  }}
                  onBlur={() => setFieldTouched(item.name, true)}
                  placeholder={t("select")}
                  isLoading={categoriesLoading}
                  isDisabled={isDisabled || categoriesLoading}
                  styles={getSelectStyles(isInvalid)}
                  noOptionsMessage={() => t("NoOptions", { defaultValue: "No categories available" })}
              />
          );
      }

      // --- NEW: Case for SubCategory Select ---
      case "select-subcategory": {
          const isInvalid = touched[item.name] && !!errors[item.name];
          
          const selectedValue = subCategoriesOptions.find(opt => String(opt.value) === String(fieldValue)) || null;
          return (
              <Select
                  id={item.name}
                  name={item.name}
                  options={subCategoriesOptions}
                  value={selectedValue}
                  onChange={(selectedOption) => {
                      setFieldValue(item.name, selectedOption ? selectedOption.value : '');
                  }}
                  onBlur={() => setFieldTouched(item.name, true)}
                  placeholder={t("select")}
                  isLoading={subCategoriesLoading}
                  isDisabled={isDisabled || subCategoriesLoading || !values.CategoryID} // Disable if no category is selected
                  styles={getSelectStyles(isInvalid)}
                  noOptionsMessage={() => !values.CategoryID 
                      ? t("Please select a category first") 
                      : t("NoOptions", { defaultValue: "No sub-categories available" })}
              />
          );
      }

      case "multiselect-create-dropdown": {
      const isInvalid = touched[item.name] && !!errors[item.name];

      // Ensure TagID is always an array of objects for Formik
      const selectedTagObjects = allTags.filter(tagOption =>
          Array.isArray(values[item.name])
              ? values[item.name].some(
                  v => (typeof v === "object" ? v.value : v) === tagOption.value
                )
              : false
      );

      const customSelectStyles = {
          control: (base, state) => ({
              ...base,
              borderColor: isInvalid ? '#f46a6a' : state.isFocused ? '#80bdff' : '#ced4da',
              '&:hover': { borderColor: isInvalid ? '#f46a6a' : '#adb5bd' },
              boxShadow: isInvalid ? '0 0 0 0.2rem rgba(244, 106, 106, 0.25)' : (state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none'),
              direction: isRTL ? 'rtl' : 'ltr',
          }),
          menu: base => ({ ...base, textAlign: isRTL ? 'right' : 'left' }),
          multiValue: base => ({ ...base, direction: isRTL ? 'rtl' : 'ltr' }),
      };

      return (
          <CreatableSelect
              id={item.name}
              name={item.name}
              isMulti
              options={allTags}
              value={selectedTagObjects}
              inputValue={tagInputValue}
              onInputChange={setTagInputValue}
              onChange={(selectedOptions) => {
                  setFieldValue(item.name, selectedOptions || []);
              }}
              onBlur={() => setFieldTouched(item.name, true)}
              placeholder={t("Select or Create tags")}
              formatCreateLabel={inputValue => `${t('Create')} "#${inputValue}"`}
              styles={customSelectStyles}
              isLoading={isTagCreating || tagCreationLoading}
              onCreateOption={handleCreateTag}
              isDisabled={isDisabled || isTagCreating || tagCreationLoading}
          />
      );
      }

      case "select-box":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            disabled={isDisabled}
            required={item.required}
          >
            <option value="">{t("Select")} {t(item.labelName)}</option>
            {documentsType?.map((option) => (
              <option key={option.ID} value={option.ID}>
                {isRTL ? t(option.NameAr) : t(option.NameEn)}
              </option>
            ))}
          </Input>
        );

      case "select-box-parent":
        // Disable ParentCategory if CategoryType is 'Article' (2) or 'Book' (3)
        const isParentCategoryDisabled = isDisabled || values.CategoryType === '2' || values.CategoryType === '3';
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            disabled={isParentCategoryDisabled}
            required={item.required}
          >
            <option value="">{t("None")}</option>
            {parentCategories.map((option) => (
              <option key={option.ID} value={option.ID}>
                {isRTL ? t(option.NameAr) : option.NameEn}
              </option>
            ))}
          </Input>
        );

      case "color-picker": // Added case for color picker
          return (
            <Input
              type="color"
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue || defaultColor} // Use defaultColor if value is empty
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-control form-control-color" // Use form-control-color class for styling
              invalid={touched[item.name] && !!errors[item.name]}
              required={item.required}
            />
          );
        default:
          return null;
    }
  };

  return (
    <Row>
      {labels.map((item) => {
        
        return (
          <Col sm={6} key={item.name}>
            <div className="mb-3">
              {item.labelName !== "Mobile" && (
                <label htmlFor={item.name} className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>}
                </label>
              )} 
              {/* Render the field */}
              {renderField(item)}
              {/* Display error for the main field */}
              {item.labelName !== "Mobile" && touched[item.name] && errors[item.name] && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[item.name])}</div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

export default EditDocDetails